import { useCallback, useState } from 'react';

/**
 * Which New-ticket layout this browser uses.
 *
 * 'classic' — the original two-column form: fields on the left, a stack of
 *             property boxes down the right.
 * 'guided'  — the same fields split across numbered tabs with Next/Back, so
 *             there is no long right-hand rail to scroll.
 *
 * Per browser, like the theme and the notification preference. Classic stays
 * the default: nobody gets a new layout they did not ask for.
 */
export type TicketFormLayout = 'classic' | 'guided';

const KEY = 'ctt-ticket-form-layout';

export function readTicketFormLayout(): TicketFormLayout {
  try {
    return localStorage.getItem(KEY) === 'guided' ? 'guided' : 'classic';
  } catch {
    return 'classic'; // private mode / storage blocked
  }
}

export function writeTicketFormLayout(layout: TicketFormLayout): void {
  try {
    localStorage.setItem(KEY, layout);
  } catch {
    /* preference just will not stick this session */
  }
}

export function useTicketFormLayout(): [TicketFormLayout, (l: TicketFormLayout) => void] {
  const [layout, setLayout] = useState<TicketFormLayout>(readTicketFormLayout);
  const change = useCallback((l: TicketFormLayout) => {
    setLayout(l);
    writeTicketFormLayout(l);
  }, []);
  return [layout, change];
}
